(() => {
  'use strict';

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const productTags = (product) => {
    if (Array.isArray(product.tags)) return product.tags.map((tag) => String(tag).trim());
    return String(product.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
  };

  const imageFor = (product) => {
    const image = product.images?.[0] || {};
    return {
      src: image.src || '',
      alt: image.alt || `${product.title}, MatchBoox-miniatuurboek`,
    };
  };

  const fundAuthors = () => {
    const source = document.querySelector('[data-mb-fonds-authors]');
    if (!source) return {};
    try { return JSON.parse(source.textContent); } catch (_error) { return {}; }
  };

  const authorFor = (handle) => {
    if (handle === 'exclusieve-ezels-serie') return 'Mensje van Keulen';
    const authors = Object.entries(fundAuthors())
      .filter(([, handles]) => Array.isArray(handles) && handles.includes(handle))
      .map(([name]) => name)
      .join(', ');
    return authors;
  };

  const priceFor = (product) => {
    const raw = product.variants?.[0]?.price ?? product.price ?? 0;
    const numeric = Number(raw);
    const amount = String(raw).includes('.') || String(raw).includes(',') ? numeric : numeric / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(Number.isFinite(amount) ? amount : 0);
  };

  const cardHtml = (product, index = 0) => {
    const image = imageFor(product);
    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
    let title = String(product.title || '').trim();
    let caption = authorFor(product.handle) || String(product.vendor || '').trim();
    if (!caption || /^matchboox$/i.test(caption)) caption = '';
    if (!caption && title.includes(' – ') && !/serie/i.test(title)) {
      const parts = title.split(' – ');
      caption = parts.shift().trim();
      title = parts.join(' – ').trim();
    }
    const imageMarkup = image.src
      ? `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" loading="${index < 5 ? 'eager' : 'lazy'}" decoding="async" width="1000" height="700">`
      : '';
    const price = priceFor(product);

    return `
      <article class="mb-clean-product-card" data-mb-clean-product-card data-product-handle="${escapeHtml(product.handle)}">
        <a class="mb-clean-product-card__link" href="${productUrl}">
          <span class="mb-clean-product-card__media" aria-label="Bekijk ${escapeHtml(title)}">
            ${imageMarkup}
          </span>
          <strong class="mb-clean-product-card__title">${escapeHtml(title)}</strong>
          ${caption ? `<span class="mb-clean-product-card__caption">${escapeHtml(caption)}</span>` : ''}
        </a>
        <div class="mb-clean-product-card__footer">
          <span class="mb-clean-product-card__price">${escapeHtml(price)}</span>
          <button type="button" class="mb-quick-add mb-clean-product-card__quick-add" data-mb-quick-add data-product-handle="${escapeHtml(product.handle)}" data-default-label="Toevoegen aan winkelmandje" aria-label="Toevoegen aan winkelmandje" title="Toevoegen aan winkelmandje"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 8.5h11l-1 10h-9l-1-10Zm2.5 0V6.7a3 3 0 0 1 6 0v1.8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        </div>
      </article>`;
  };

  const fetchProducts = async () => {
    const products = [];
    const root = window.Shopify?.routes?.root || '/';
    for (let page = 1; page <= 10; page += 1) {
      const response = await fetch(`${root}products.json?limit=250&page=${page}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Producten konden niet worden geladen (${response.status})`);
      const batch = (await response.json()).products || [];
      products.push(...batch);
      if (batch.length < 250) break;
    }
    return products;
  };

  const catalogueData = () => {
    const source = document.querySelector('script[id^="MatchBooxCarouselData-"]');
    if (!source) return {};
    try { return JSON.parse(source.textContent); } catch (_error) { return {}; }
  };

  const controlledCategory = (handle) => (catalogueData().categories || [])
    .find((category) => category.handle === handle);

  const catalogueNumbers = () => {
    const data = catalogueData();
    const map = new Map();
    ['large', 'loose', 'series'].forEach((group) => {
      (data[group] || []).forEach((item) => {
        const number = Number(String(item.number || '').match(/\d+/)?.[0] || 0);
        if (item.handle && number) map.set(item.handle, number);
      });
    });
    (data.categories || []).flatMap((category) => category.items || []).forEach((item) => {
      const number = Number(String(item.number || '').match(/\d+/)?.[0] || 0);
      if (item.handle && number && !map.has(item.handle)) map.set(item.handle, number);
    });
    return map;
  };

  const skuNumber = (product, knownNumbers) => {
    const known = knownNumbers.get(product.handle);
    if (known) return known;
    const sku = product.variants?.find((variant) => variant.sku)?.sku || '';
    const values = String(sku).match(/\d+/g) || [];
    if (values.length) return Number(values.at(-1));
    const titleValues = String(product.title || '').match(/(?:MB\s*)?(\d{2,4})/gi) || [];
    return Number(String(titleValues.at(-1) || '').match(/\d+/)?.[0] || 0);
  };

  const init = async (section) => {
    if (!section || section.dataset.mbTagReady === 'true') return;
    section.dataset.mbTagReady = 'true';
    const requiredTag = section.dataset.collectionTag;
    const handle = section.dataset.collectionHandle;
    const grid = section.querySelector('[data-mb-tag-grid]');
    const status = section.querySelector('[data-mb-tag-status]');

    try {
      const allProducts = await fetchProducts();
      const category = controlledCategory(handle);
      let products;

      if (handle === 'alle-matchboox') {
        /* Toon het volledige fonds in nummer-volgorde. De fondslijst is hiervoor
           leidend, zodat deze pagina exact dezelfde 178 uitgaven bevat als het
           keuzeblok en begint bij MatchBoox 001. */
        const productsByHandle = new Map(allProducts.map((product) => [product.handle, product]));
        products = [...(catalogueData().large || [])]
          .sort((a, b) => Number(a.number || 0) - Number(b.number || 0))
          .map((item) => productsByHandle.get(item.handle))
          .filter(Boolean);
      } else if (category?.items?.length) {
        /* De beheerde MatchBoox-catalogus is leidend voor themapagina's. Shopify-tags
           verschillen per import en veroorzaakten daardoor lege of onjuiste lijsten. */
        const productsByHandle = new Map(allProducts.map((product) => [product.handle, product]));
        products = category.items.map((item) => productsByHandle.get(item.handle)).filter(Boolean);
      } else {
        products = allProducts.filter((product) => {
          const tags = productTags(product);
          return tags.includes(requiredTag) && !tags.includes('Illustrator-editie') && !tags.includes('Vormgever-editie') && !tags.includes('Alternatieve-editie');
        });
      }

      if (handle !== 'alle-matchboox' && handle !== 'meest-verkocht') {
        const knownNumbers = catalogueNumbers();
        products.sort((a, b) => skuNumber(b, knownNumbers) - skuNumber(a, knownNumbers)
          || String(b.published_at || b.created_at || '').localeCompare(String(a.published_at || a.created_at || '')));
      }

      if (handle === 'meest-verkocht') {
        const bestsellerHandles = [
          'nescio-in-bergen-serie',
          'facing-mick-keith-ron-charlie',
          'm-vasalis-serie',
          'trijntje-fop-serie',
          'the-fab-four-serie',
          'ramses-shaffy-serie',
          'johan-cruijff-serie',
          'jma-biesheuvel-collection',
          'annie-mg-schmidt-collection',
          'collection-c-buddingh',
          'drs-p-serie',
          'vier-grote-schrijvers'
        ];
        const bestsellerRank = new Map(bestsellerHandles.map((productHandle, index) => [productHandle, index]));
        products.sort((left, right) =>
          (bestsellerRank.get(left.handle) ?? Number.MAX_SAFE_INTEGER)
          - (bestsellerRank.get(right.handle) ?? Number.MAX_SAFE_INTEGER));
      }

      if (handle === 'losse-boekjes') {
        products = products.filter((product) => !/ontwerp[\s-]*zelf/i.test(`${product.handle} ${product.title}`));
      }

      if (handle === 'series') {
        products = products.filter((product) => product.handle !== 'exclusieve-ezels-serie');
      }

      if (handle === 'series' || handle === 'losse-boekjes') {
        const knownNumbers = catalogueNumbers();
        products.sort((a, b) => skuNumber(b, knownNumbers) - skuNumber(a, knownNumbers)
          || String(b.published_at || b.created_at || '').localeCompare(String(a.published_at || a.created_at || '')));
      }

      if (!products.length) {
        status.textContent = 'Voor deze selectie zijn momenteel geen boekjes gevonden.';
        return;
      }

      grid.innerHTML = products.map(cardHtml).join('');
      grid.hidden = false;
      status.hidden = true;
      document.dispatchEvent(new CustomEvent('matchboox:clean-cards', { detail: { root: grid } }));
    } catch (error) {
      console.error(error);
      status.textContent = 'De boekjes konden niet worden geladen. Ververs deze pagina om het opnieuw te proberen.';
    }
  };

  const initAll = (root = document) => root.querySelectorAll('[data-mb-tag-collection]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
